import { useCallback } from "react";

import { Action, Module, Run, Stack } from "types/generated";
import { hasAtLeastOneRequiredAction } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import useAacFlags from "hooks/useAacFlags";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";

import { isRunReviewable } from "../useReviewRun/accessValidation";
import useHasEntityWriteAccess from "./useHasEntityWriteAccess";

const REVIEW_ACCESS_TYPES = [Action.RunReview];

const hasReviewAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(REVIEW_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can review a run.
 * A user needs either write access to the entity or the RunReview permission to review a run.
 * @returns {(run?: Run, entity?: Module | Stack) => boolean} Function that takes an optional run and entity and returns a boolean indicating if user can review the run
 */
const useCanReviewRun = (): ((run?: Run, entity?: Module | Stack) => boolean) => {
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const { customAccessRoles } = useAacFlags();
  const { viewer } = useTypedContext(AccountContext);
  return useCallback(
    (run?: Run, entity?: Module | Stack) => {
      if (!isRunReviewable(run)) {
        return false;
      }

      if (!entity) {
        return false;
      }

      const hasAccessToReview =
        hasEntityWriteAccess(entity) ||
        (customAccessRoles && hasReviewAccess(viewer, entity?.spaceDetails?.id || entity?.space));

      if (hasAccessToReview) {
        return true;
      }

      return false;
    },
    [hasEntityWriteAccess, customAccessRoles, viewer]
  );
};

export default useCanReviewRun;
